import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, Pencil, ScanText, ScanSearch, GraduationCap, Image } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";

const ICON = { text: ScanText, object: ScanSearch, study: GraduationCap, scene: Image };

export default function LensArchive() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/lens/scans").then((r) => setScans(r.data.scans || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const rename = async (s) => {
    const title = window.prompt("Rename scan", s.title);
    if (title == null) return;
    try { await api.patch(`/lens/scans/${s.scan_id}`, { title }); load(); } catch { toast.error("Rename failed."); }
  };
  const remove = async (s) => {
    if (!window.confirm("Delete this scan?")) return;
    try { await api.delete(`/lens/scans/${s.scan_id}`); setOpen(null); load(); toast.success("Deleted"); } catch { toast.error("Delete failed."); }
  };
  const reopen = async (s) => {
    try { const r = await api.get(`/lens/scans/${s.scan_id}`); setOpen(r.data); } catch { toast.error("Could not open scan."); }
  };

  return (
    <AppLayout>
      <Seo title="FluentLens Archive" />
      <div className="max-w-3xl mx-auto" data-testid="lens-archive">
        <Link to="/lens" className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 mb-3"><ArrowLeft className="w-4 h-4" /> FluentLens</Link>
        <h1 className="font-cabinet font-extrabold text-3xl mb-1">FluentLens Archive</h1>
        <p className="text-gray-600 text-sm mb-5">Your private scans in the active language. Reopen, revisit vocabulary, or retry a study session.</p>

        {loading ? <div className="nb-card p-10 text-center text-gray-400">Loading…</div>
          : scans.length === 0 ? <div className="nb-card p-10 text-center text-gray-500">No scans yet. Try Text, Object or Study Lens.</div>
          : (
            <div className="space-y-2" data-testid="archive-list">
              {scans.map((s) => {
                const Ic = ICON[s.type] || ScanText;
                return (
                  <div key={s.scan_id} className="nb-card p-3 flex items-center justify-between gap-3" data-testid={`archive-item-${s.scan_id}`}>
                    <button onClick={() => reopen(s)} className="flex items-center gap-3 text-left flex-1 min-w-0">
                      <Ic className="w-6 h-6 shrink-0" />
                      <div className="min-w-0"><div className="font-bold truncate">{s.title}</div><div className="text-xs text-gray-500">{s.type} · {s.target_lang} · {new Date(s.created_at).toLocaleDateString()}{s.saved_vocab?.length ? ` · ${s.saved_vocab.length} words` : ""}</div></div>
                    </button>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => rename(s)} className="p-2 border-2 border-ink rounded-lg" data-testid={`archive-rename-${s.scan_id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(s)} className="p-2 border-2 border-ink rounded-lg text-coral" data-testid={`archive-delete-${s.scan_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        {open && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink/60" onClick={() => setOpen(null)}>
            <div className="nb-card max-w-lg w-full p-5 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="archive-detail">
              <div className="font-cabinet font-extrabold text-xl">{open.title}</div>
              <div className="text-xs text-gray-500 mb-3">{open.type} · {open.target_lang}</div>
              {open.object_label && <div className="nb-tag bg-peach w-fit mb-2" dir="auto">{open.object_label}</div>}
              {open.scene_summary && <p className="text-sm bg-sky/30 border-2 border-ink rounded-xl p-3 mb-2" dir="auto">{open.scene_summary}</p>}
              {open.extracted_text && <p className="whitespace-pre-wrap text-sm bg-paper border-2 border-ink rounded-xl p-3" dir="auto">{open.extracted_text}</p>}
              {open.study_questions?.length > 0 && (
                <div className="mt-3"><div className="font-bold text-sm mb-1">Study questions</div>
                  <ul className="text-sm space-y-1 list-disc list-inside">{open.study_questions.map((q, i) => <li key={q.id || i}>{q.prompt}</li>)}</ul>
                  <Link to="/lens/study" state={{ text: open.extracted_text }} className="nb-btn-primary py-2 px-4 text-sm mt-3 inline-flex" data-testid="archive-retry-study">Retry study session</Link>
                </div>
              )}
              {open.saved_vocab?.length > 0 && <div className="mt-3 text-sm text-gray-600">Saved words: {open.saved_vocab.join(", ")} · <Link to="/vocab" className="text-coral font-bold">Vocab Bank</Link></div>}
              <button onClick={() => setOpen(null)} className="nb-btn-outline w-full py-2 mt-4">Close</button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
